import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    DefaultButton,
    PrimaryButton,
    Panel,
    PanelType,
    Separator,
    Stack,
    Calendar,
    Callout,
    Text,
    defaultCalendarStrings,
    ICalendarStrings
} from '@fluentui/react';

import { Holidays } from '../../../../../api-client';
import { DateTimeEnchanter } from '../../../../../utils/data-enchanters';
import { useClassNames } from './iai-calendar-holidays.classNames';
import { TRANSLATION_KEYS } from './localization/translation-keys';

interface IEventCalendarHolidaysDrawer {
    holidays?: Holidays;
    disabled?: boolean;
    drawerVisible?: boolean;
    onDrawerConfirm: (dataItem: Holidays) => void;
    onDrawerDismiss: () => void;
};

const CalendarHolidaysDrawer: React.FunctionComponent<IEventCalendarHolidaysDrawer> = ({
    holidays,
    disabled,
    drawerVisible,
    onDrawerConfirm,
    onDrawerDismiss
}) => {
    const {
        i18n,
        t: translate
    } = useTranslation();

    const {
        calloutContainer,
        calloutBeakCurtain,
        selected
    } = useClassNames();

    const _handleDismiss = () => {
        setSelectedDates(holidays?.dates ?? []);
        onDrawerDismiss();
    }

    const disableCondition = disabled;

    const [selectedDates, setSelectedDates] = useState<string[]>([]);
    const [currentDate, setCurrentDate] = useState<Date | undefined>(undefined);

    useEffect(() => {
        setSelectedDates(holidays?.dates ?? []);
    }, [holidays])

    const onConfirmDate = (date: Date) => {
        const exists = selectedDates.some(d => d === DateTimeEnchanter.formatStandardDate(date, 'en-US'));
        const newDates = exists
            ? selectedDates.filter(d => d !== DateTimeEnchanter.formatStandardDate(date, 'en-US'))
            : [...selectedDates, DateTimeEnchanter.formatStandardDate(date, 'en-US')];
        setSelectedDates(newDates);
        setCurrentDate(undefined);
    };

    const onSelectDate = (date: Date) => {
        setCurrentDate(date);
    };

    const isDateSelected = (date: Date) =>
        selectedDates.some(d => d === DateTimeEnchanter.formatStandardDate(date, 'en-US'));

    const handleSubmit = () => {
        let solutionData = {
            ...holidays,
            dates: selectedDates,
        } as Holidays;

        onDrawerConfirm(solutionData);
    }

    const localizedStrings: ICalendarStrings = {
        ...defaultCalendarStrings,
        days: [
            translate("calendar.days.sun"),
            translate("calendar.days.mon"),
            translate("calendar.days.tue"),
            translate("calendar.days.wed"),
            translate("calendar.days.thu"),
            translate("calendar.days.fri"),
            translate("calendar.days.sat")
        ],
        shortDays: [
            translate("calendar.shortDays.sun"),
            translate("calendar.shortDays.mon"),
            translate("calendar.shortDays.tue"),
            translate("calendar.shortDays.wed"),
            translate("calendar.shortDays.thu"),
            translate("calendar.shortDays.fri"),
            translate("calendar.shortDays.sat")
        ],
        months: [
            translate("calendar.months.january"),
            translate("calendar.months.february"),
            translate("calendar.months.march"),
            translate("calendar.months.april"),
            translate("calendar.months.may"),
            translate("calendar.months.june"),
            translate("calendar.months.july"),
            translate("calendar.months.august"),
            translate("calendar.months.september"),
            translate("calendar.months.october"),
            translate("calendar.months.november"),
            translate("calendar.months.december"),
        ],
        shortMonths: [
            translate("calendar.shortMonth.january"),
            translate("calendar.shortMonth.february"),
            translate("calendar.shortMonth.march"),
            translate("calendar.shortMonth.april"),
            translate("calendar.shortMonth.may"),
            translate("calendar.shortMonth.june"),
            translate("calendar.shortMonth.july"),
            translate("calendar.shortMonth.august"),
            translate("calendar.shortMonth.september"),
            translate("calendar.shortMonth.october"),
            translate("calendar.shortMonth.november"),
            translate("calendar.shortMonth.december")
        ],
        goToToday: translate("calendar.goToToday"),
    };

    return (
        <Panel
            type={PanelType.custom}
            customWidth='500px'
            headerText={translate(TRANSLATION_KEYS.Heading)}
            isOpen={drawerVisible}
            isBlocking={true}
            isFooterAtBottom={true}
            onDismiss={_handleDismiss}
            onRenderFooterContent={() => (
                <div className="text-end">
                    <DefaultButton
                        disabled={disableCondition}
                        iconProps={{ iconName: 'Cancel' }}
                        styles={{ root: { marginRight: 10 } }}
                        onClick={_handleDismiss}
                    >
                        {translate(TRANSLATION_KEYS.DismissText)}
                    </DefaultButton>
                    <PrimaryButton
                        type="submit"
                        disabled={disableCondition}
                        iconProps={{ iconName: 'CheckMark' }}
                        onClick={handleSubmit}
                    >
                        {translate(TRANSLATION_KEYS.ConfirmText)}
                    </PrimaryButton>
                </div>
            )}
        >
            <Separator />

            <Stack tokens={{ childrenGap: 15 }} horizontalAlign='center'>
                <Calendar
                    id='holidays-calendar'
                    highlightSelectedMonth
                    showGoToToday
                    onSelectDate={onSelectDate}
                    value={undefined}
                    calendarDayProps={{
                        customDayCellRef: (element, date) => {
                            if (isDateSelected(date) && element) {
                                element.classList.add(selected);
                            } else {
                                if (element) {
                                    element.classList.remove(selected);
                                }
                            }
                        },
                        styles: {
                            daySelected: {
                                backgroundColor: '#f7C1D0',
                                borderRadius: '100% !important',
                                selectors: {
                                    ':hover': {
                                        backgroundColor: '#f7C1D0 !important',
                                    },
                                    ':focus': {
                                        backgroundColor: '#f7C1D0 !important',
                                    },
                                    ':active': {
                                        backgroundColor: '#f7C1D0 !important',
                                    }
                                }
                            },
                            dayCell: {
                                selectors: {
                                    ':hover': {
                                        backgroundColor: '#f7C1D0 !important',
                                    },
                                    ':focus': {
                                        backgroundColor: '#f7C1D0 !important',
                                    },
                                    ':active': {
                                        backgroundColor: '#f7C1D0 !important',
                                    }
                                }
                            }
                        }
                    }}
                    strings={localizedStrings}
                />

                {currentDate && (
                    <Callout
                        target={`#holidays-calendar`}
                        className={calloutContainer}
                        styles={{
                            beak: calloutBeakCurtain,
                            beakCurtain: calloutBeakCurtain,
                            calloutMain: calloutBeakCurtain
                        }}
                        ariaLabelledBy={'holidays label'}
                        ariaDescribedBy={'holidays content'}
                        role="dialog"
                        gapSpace={0}
                    >
                        <Text block variant="xLarge" className="mb-3">
                            {translate(TRANSLATION_KEYS.DialogContent).replace("#ACTION#", (selectedDates.some(d => d === DateTimeEnchanter.formatStandardDate(currentDate, 'en-US')) ? translate(TRANSLATION_KEYS.RemoveContent) : translate(TRANSLATION_KEYS.AddContent))).replace("#DATE#", DateTimeEnchanter.formatStandardDate(currentDate, i18n.language))}
                        </Text>
                        <Stack horizontal tokens={{ childrenGap: 15 }}>
                            <DefaultButton onClick={() => {
                                setCurrentDate(undefined);
                            }}>
                                {translate(TRANSLATION_KEYS.DismissText)}
                            </DefaultButton>

                            <PrimaryButton onClick={() => {
                                onConfirmDate(currentDate);
                            }} >
                                {translate(TRANSLATION_KEYS.ApplyText)}
                            </PrimaryButton>
                        </Stack>
                    </Callout>
                )}
            </Stack>
        </Panel>
    );
}

export default CalendarHolidaysDrawer;